import React, { useState, useEffect, useRef } from 'react';
import { Card, BlockStack, InlineStack, Text, Button } from '@shopify/polaris';
import {
  getAppUpdateAutoRefresh,
  setUpdateDismissed,
  isUpdateDismissed,
} from '../utils/appUpdatePrefs';

// Auto-refresh only fires once the user has been hands-off this long (or the
// tab is hidden), so nobody loses their place mid-pick.
const IDLE_MS = 60 * 1000;
const IDLE_POLL_MS = 5 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];

/**
 * Small corner card shown when hosting is already serving a newer build, so
 * the Refresh action is guaranteed to work. Replaces the old full-width top
 * banner that covered the header and nagged mid-deploy.
 */
function AppUpdateToast({
  updateReady,
  remoteVersion,
  targetKey,
  refreshLoopBlocked,
  onRefresh,
}) {
  const [dismissed, setDismissed] = useState(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  // Re-arm the prompt when a newer build supersedes the dismissed one.
  useEffect(() => {
    if (targetKey) setDismissed(isUpdateDismissed(targetKey));
  }, [targetKey]);

  const autoRefreshArmed =
    updateReady && !dismissed && !refreshLoopBlocked && getAppUpdateAutoRefresh();

  useEffect(() => {
    if (!autoRefreshArmed) return undefined;

    let lastActivity = Date.now();
    const bumpActivity = () => { lastActivity = Date.now(); };
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, bumpActivity, { passive: true })
    );

    const intervalId = window.setInterval(() => {
      const idle = Date.now() - lastActivity >= IDLE_MS;
      if (document.visibilityState === 'hidden' || idle) {
        window.clearInterval(intervalId);
        onRefreshRef.current?.();
      }
    }, IDLE_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, bumpActivity));
    };
  }, [autoRefreshArmed]);

  if (!updateReady || dismissed) return null;

  const handleLater = () => {
    setUpdateDismissed(targetKey);
    setDismissed(true);
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        maxWidth: 320,
        animation: 'app-update-toast-in 240ms ease-out',
      }}
    >
      <style>{`
        @keyframes app-update-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <Card padding="300">
        <BlockStack gap="200">
          <InlineStack gap="150" blockAlign="center" wrap={false}>
            <span aria-hidden="true">🔄</span>
            <Text as="p" variant="bodySm" fontWeight="semibold">
              {remoteVersion ? `Update ready · v${remoteVersion}` : 'Update ready'}
            </Text>
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            {refreshLoopBlocked
              ? 'The last refresh kept the old version — try a hard refresh (Ctrl+Shift+R).'
              : autoRefreshArmed
                ? 'Refreshes itself when you step away. Your session is kept.'
                : 'Your session is kept.'}
          </Text>
          <InlineStack gap="200">
            <Button size="slim" variant="primary" onClick={onRefresh}>
              {refreshLoopBlocked ? 'Try again' : 'Refresh'}
            </Button>
            <Button size="slim" variant="tertiary" onClick={handleLater}>
              Later
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    </div>
  );
}

export default AppUpdateToast;
